import { useApi } from '@/api';
import { Link, TableSkeleton } from '@/components';
import { transformString } from '@/utils';
import { tokensMapping } from '@/utils/tokens';
import {
  CryptoIcon,
  CurrencyValue,
  DataTable,
  EtherScanPictogram,
  Flex,
  FormattedNumber,
  RelativeDateTime,
  WalletAddress
} from '@blockanalitica/ui';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';

export default function EventsTable({ pagination, sorting }) {
  const [data, error] = useApi({
    url: '/sbe/swaps/',
    queryParams: {
      p: pagination.currentPage,
      p_size: pagination.pageSize,
      order: sorting.currentSorting,
      operation: 'buyback'
    }
  });

  if (error) {
    throw error;
  }

  if (data) {
    const columns: TableColumn[] = [
      {
        header: 'Wallet',
        getCell: (row) => (
          <Link
            to={`/tokens/0x56072c95faa701256059aa122697b133aded9279/holders/${row.receiver}`}>
            <WalletAddress
              options={{
                inline: true,
                sizes: { blockie: '0' }
              }}
              address={row.receiver}
            />
          </Link>
        ),
        columnSize: ['minmax(100px, auto)']
      },
      {
        header: 'Event',
        getCell: (row) => transformString(row.event) + ' 🔥',
        columnSize: ['minmax(80px, auto)']
      },
      {
        header: (
          <Flex
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap="3xs">
            USDS
            <CryptoIcon
              name="USDS"
              address={tokensMapping('USDS').address}
              size="0"
            />
          </Flex>
        ),
        getCell: (row) => (
          <CurrencyValue
            value={row.usds_amount}
            options={{
              iconSize: '0',
              trend: true
            }}
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'usds_amount'
      },
      {
        header: (
          <Flex
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap="3xs">
            SKY
            <CryptoIcon
              name="SKY"
              address={tokensMapping('SKY').address}
              size="0"
            />
          </Flex>
        ),
        getCell: (row) => (
          <CurrencyValue
            value={row.sky_amount}
            options={{
              iconSize: '0',
              trend: true
            }}
          />
        ),
        getCellBottom: (row) => (
          <CurrencyValue
            color="muted"
            fontSize="-1"
            value={Math.abs(row.sky_amount_usd)}
            options={{
              iconSize: '0',
              currency: 'USD'
            }}
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'sky_amount_usd'
      },
      {
        header: 'Price',
        getCell: (row) => (
          <FormattedNumber
            value={row.price}
            options={{
              formatNumberOptions: {
                notation: 'standard',
                maximumFractionDigits: 4
              },
              suffix: (
                <Flex color="muted" fontSize="-2">
                  USDS/SKY
                </Flex>
              )
            }}
          />
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'price'
      },
      {
        header: 'Time',
        getCell: (row) => (
          <Flex gap={['xs', '2xs']} color="muted">
            <RelativeDateTime value={row.datetime} />
            <a
              href={`https://etherscan.io/tx/${row.tx_hash}`}
              target="_blank"
              rel="noreferrer">
              <EtherScanPictogram size="0" />
            </a>
          </Flex>
        ),
        cellAlign: 'flex-end',
        headerAlign: 'flex-end',
        sortingField: 'datetime'
      }
    ];

    return (
      <DataTable
        rows={data['results']}
        columns={columns}
        getKey={(row) => row.order_index}
        sorting={sorting}
        pagination={{
          ...pagination,
          totalRecords: data.count
        }}
      />
    );
  }
  return <TableSkeleton />;
}
